import { useMutation, useQuery } from '@tanstack/react-query';
import type { SurveyTemplate, SurveyResults, SurveyPublic, SurveySubmission } from '@iziping/common';
import { apiClient } from '@iziping/common';
import { ENDPOINTS, QUERY_CACHE } from '@/model/endpoints';
import type { ApiError } from '@iziping/common';

export const useGetSurveyTemplate = (tournamentId: number) => {
    return useQuery<SurveyTemplate, ApiError>({
        queryKey: [QUERY_CACHE.SURVEY_TEMPLATE, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<SurveyTemplate>(ENDPOINTS.surveyTemplate(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId,
    });
};

export const useSaveSurveyTemplate = (tournamentId: number) => {
    return useMutation<SurveyTemplate, ApiError, SurveyTemplate>({
        mutationKey: [QUERY_CACHE.SAVE_SURVEY_TEMPLATE, tournamentId],
        mutationFn: async (template) => {
            const response = await apiClient.put<SurveyTemplate>(ENDPOINTS.surveyTemplate(tournamentId), template);
            return response.data;
        },
    });
};

export const useDeleteSurveyTemplate = (tournamentId: number) => {
    return useMutation<void, ApiError, void>({
        mutationKey: [QUERY_CACHE.DELETE_SURVEY_TEMPLATE, tournamentId],
        mutationFn: async () => {
            await apiClient.delete(ENDPOINTS.surveyTemplate(tournamentId));
        },
    });
};

export const useSendSurvey = (tournamentId: number) => {
    return useMutation<void, ApiError, void>({
        mutationKey: [QUERY_CACHE.SEND_SURVEY, tournamentId],
        mutationFn: async () => {
            await apiClient.post(ENDPOINTS.surveySend(tournamentId));
        },
    });
};

export const useGetSurveyResults = (tournamentId: number, enabled = true) => {
    return useQuery<SurveyResults, ApiError>({
        queryKey: [QUERY_CACHE.SURVEY_RESULTS, tournamentId],
        queryFn: async () => {
            const response = await apiClient.get<SurveyResults>(ENDPOINTS.surveyResults(tournamentId));
            return response.data;
        },
        enabled: !!tournamentId && enabled,
    });
};

export const useGetSurveyPublic = (token: string) => {
    return useQuery<SurveyPublic, ApiError>({
        queryKey: [QUERY_CACHE.SURVEY_TEMPLATE, token],
        queryFn: async () => {
            const response = await apiClient.get<SurveyPublic>(ENDPOINTS.surveyPublic(token));
            return response.data;
        },
        enabled: !!token,
    });
};

export const useExportSurveyResults = (tournamentId: number) => {
    return useMutation<void, ApiError, void>({
        mutationFn: async () => {
            const response = await apiClient.get(ENDPOINTS.surveyExport(tournamentId), {
                responseType: 'blob',
            });
            const disposition: string = response.headers['content-disposition'] ?? '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `survey_${tournamentId}.xlsx`;
            const url = URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        },
    });
};

export const useSubmitSurvey = (token: string) => {
    return useMutation<void, ApiError, SurveySubmission>({
        mutationFn: async (submission) => {
            await apiClient.post(ENDPOINTS.surveyPublic(token), submission);
        },
    });
};
